/**
 * Righe manuali dell'offerta: voci libere (codice + descrizione + prezzo) senza prodotto in anagrafica.
 *
 * In DB sono `price_list_items` con `product_id = null` e i campi `custom_*`.
 * Per non duplicare la logica di stampa/export/fatturazione, dopo il fetch le righe manuali
 * vengono normalizzate in un oggetto `products` sintetico: da lì in poi il resto del codice
 * tratta riga manuale e riga prodotto allo stesso modo.
 */

/** Unità di misura di default per una riga manuale. */
export const CUSTOM_LINE_DEFAULT_UNIT = 'pz';

/** Aliquota IVA di default per una riga manuale (nessuna anagrafica prodotto da cui ereditarla). */
export const CUSTOM_LINE_DEFAULT_VAT = 22;

export interface CustomLineFields {
  custom_code?: string | null;
  custom_name?: string | null;
  custom_description?: string | null;
  custom_unit?: string | null;
  custom_category?: string | null;
}

interface PriceListItemLike extends CustomLineFields {
  product_id?: string | null;
  price?: number | null;
  products?: unknown;
}

/** True se la riga è manuale (nessun prodotto collegato). */
export function isCustomLine(item: PriceListItemLike | null | undefined): boolean {
  return !!item && !item.product_id;
}

/**
 * Costruisce il "prodotto sintetico" di una riga manuale, con la stessa forma
 * dei prodotti reali usati da liste, PDF, Excel e fatture.
 */
export function buildCustomLineProduct(item: PriceListItemLike): Record<string, unknown> {
  return {
    id: '',
    code: item.custom_code || '',
    name: item.custom_name || '',
    description: item.custom_description || '',
    category: item.custom_category || '',
    unit: item.custom_unit || CUSTOM_LINE_DEFAULT_UNIT,
    base_price: item.price ?? 0,
    iva: CUSTOM_LINE_DEFAULT_VAT,
    photo_url: null,
    photo_thumb_url: null,
    cartone: null,
    pallet: null,
    ean: null,
    scadenza: null,
    is_custom_line: true,
  };
}

/**
 * Normalizza le righe di un'offerta: le righe manuali ricevono un `products` sintetico,
 * le righe prodotto restano invariate.
 */
export function normalizePriceListItems<T extends PriceListItemLike>(items: T[] | null | undefined): T[] {
  if (!Array.isArray(items)) return [];
  return items.map((item) =>
    isCustomLine(item) ? ({ ...item, products: buildCustomLineProduct(item) } as T) : item
  );
}

/**
 * Normalizza l'offerta completa (immutabile): ritorna una copia con `price_list_items` normalizzati.
 */
export function normalizePriceList<T extends { price_list_items?: PriceListItemLike[] | null }>(
  priceList: T | null | undefined
): T | null {
  if (!priceList) return null;
  return {
    ...priceList,
    price_list_items: normalizePriceListItems(priceList.price_list_items),
  };
}

/** Campi `custom_*` da copiare quando si duplica una riga offerta. */
export function pickCustomLineFields(item: CustomLineFields): Required<CustomLineFields> {
  return {
    custom_code: item.custom_code ?? null,
    custom_name: item.custom_name ?? null,
    custom_description: item.custom_description ?? null,
    custom_unit: item.custom_unit ?? null,
    custom_category: item.custom_category ?? null,
  };
}
